import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, Star, Clock, CheckCircle, Phone, ArrowRight, Loader2,
  Droplet, Zap, Thermometer, Wrench, Sparkles, Car, BadgeCheck, Search, 
  Briefcase, Sun, Cloud, CloudRain, Wind, FileText, Building2, AlertTriangle,
  TrendingUp, Users, Calendar, Shield, Home, Bug, Hammer, Trees, Truck, DoorOpen, Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LocalSEOSchema from "@/components/LocalSEOSchema";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  plumbing: Droplet,
  electrical: Zap,
  hvac: Thermometer,
  handyman: Wrench,
  cleaning: Sparkles,
  mechanics: Car,
  roofing: Home,
  "pest-control": Bug,
  remodeling: Hammer,
  landscaping: Trees,
  moving: Truck,
  "garage-door": DoorOpen,
  "appliance-repair": Settings
};

const serviceNames = {
  plumbing: "Plumbing",
  electrical: "Electrical",
  hvac: "HVAC",
  handyman: "Handyman",
  cleaning: "Cleaning",
  mechanics: "Auto Repair",
  roofing: "Roofing",
  "pest-control": "Pest Control",
  remodeling: "Remodeling",
  landscaping: "Landscaping",
  moving: "Moving",
  "garage-door": "Garage Door",
  "appliance-repair": "Appliance Repair"
};

const weatherIcons = {
  "Sunny": Sun,
  "Clear": Sun,
  "Partly Cloudy": Cloud,
  "Cloudy": Cloud,
  "Rainy": CloudRain,
  "Windy": Wind
};

const CityServicePage = () => {
  const { citySlug, service } = useParams();
  const navigate = useNavigate();
  const [seoData, setSeoData] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySlug, service]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Primary SEO data - required for page to render
      const [seoRes, citiesRes] = await Promise.all([
        axios.get(`${API}/seo/city/${citySlug}/service/${service}`),
        axios.get(`${API}/seo/all-cities`)
      ]);
      
      setSeoData(seoRes.data);
      setAllCities(citiesRes.data);

      // Secondary data - workers and jobs (optional, don't fail page if unavailable)
      try {
        const workersRes = await axios.get(`${API}/workers/public`);
        const filteredWorkers = workersRes.data.filter(w => 
          w.services?.includes(service) && 
          (w.location?.toLowerCase().includes(seoRes.data.city?.city?.toLowerCase()) ||
           w.service_areas?.some(area => area.toLowerCase().includes(seoRes.data.city?.city?.toLowerCase())))
        );
        setWorkers(filteredWorkers.slice(0, 6));
      } catch (e) {
        console.log("Workers data unavailable");
      }

      try {
        const jobsRes = await axios.get(`${API}/public-jobs`);
        const filteredJobs = jobsRes.data.filter(j => 
          j.service_category === service && 
          j.location?.toLowerCase().includes(seoRes.data.city?.city?.toLowerCase())
        );
        setJobs(filteredJobs.slice(0, 4));
      } catch (e) {
        console.log("Jobs data unavailable");
      }

    } catch (error) {
      console.error("Error fetching SEO data:", error);
      toast.error("Failed to load page data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container-custom py-20 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8]" />
        </div>
      </div>
    );
  }

  const Icon = serviceIcons[service] || Wrench;
  const serviceName = serviceNames[service] || service?.charAt(0).toUpperCase() + service?.slice(1);
  const cityData = seoData?.city || {};
  const weather = seoData?.weather || {};
  const WeatherIcon = weatherIcons[weather.condition] || Sun;
  const seasonalDemand = seoData?.seasonal_demand || {};
  const buildingCodes = seoData?.building_codes || {};
  const stats = seoData?.stats || {};

  // Generate unique page content
  const pageTitle = `${serviceName} Services in ${cityData.city}, ${cityData.state_abbr} | Find Local ${serviceName} Pros`;
  const metaDescription = `Find trusted ${serviceName.toLowerCase()} contractors in ${cityData.city}, ${cityData.state_abbr}. ${stats.workers_count || 10}+ verified pros, ${stats.avg_rating || 4.5} avg rating. Get free quotes today!`;
  const canonicalUrl = `https://localpros.com/${citySlug}/${service}`;

  return (
    <div className="min-h-screen bg-white" data-testid="city-service-page">
      {/* SEO Schema Markup */}
      <LocalSEOSchema
        schema={seoData?.schema}
        pageTitle={pageTitle}
        metaDescription={metaDescription}
        canonicalUrl={canonicalUrl}
        city={cityData.city}
        state={cityData.state_abbr}
        service={service}
      />
      
      <Navbar />

      {/* Hero Section with Weather */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-[#e8f0fe] to-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-[#5f6368] mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-[#1a73e8]">Home</Link>
                <span>/</span>
                <Link to={`/${citySlug}/plumbing`} className="hover:text-[#1a73e8]">{cityData.city}</Link>
                <span>/</span>
                <span className="text-[#202124]">{serviceName}</span>
              </nav>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#dadce0] mb-6">
                <Icon className="w-5 h-5 text-[#1a73e8]" />
                <span className="text-[#5f6368]">{serviceName} Services</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-medium text-[#202124] mb-4">
                Best {serviceName} Services in {cityData.city}, {cityData.state_abbr}
              </h1>
              
              <p className="text-lg text-[#5f6368] mb-6">
                Find trusted, verified {serviceName.toLowerCase()} contractors serving {cityData.city} and surrounding areas including {cityData.nearby_cities?.slice(0, 3).join(", ")}. Get free quotes from local professionals today.
              </p>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#1a73e8]" />
                  <span className="font-medium text-[#202124]">{stats.workers_count || 10}+</span>
                  <span className="text-[#5f6368]">Local Pros</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#f9ab00] fill-[#f9ab00]" />
                  <span className="font-medium text-[#202124]">{stats.avg_rating || 4.5}</span>
                  <span className="text-[#5f6368]">Avg Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#1e8e3e]" />
                  <span className="font-medium text-[#202124]">{stats.open_jobs || 5}+</span>
                  <span className="text-[#5f6368]">Open Jobs</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate(`/request?service=${service}&location=${cityData.city}`)}
                  className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-8 py-3 h-auto text-base"
                  data-testid="get-quotes-btn"
                >
                  Get Free Quotes
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/jobs?service=${service}`)}
                  className="rounded-full px-8 py-3 h-auto text-base border-[#dadce0]"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  View {stats.open_jobs || 5}+ Jobs
                </Button>
              </div>
            </div>

            {/* Weather & Demand Card */}
            <div className="bg-white rounded-2xl border border-[#dadce0] p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-[#5f6368]">{cityData.city}, {cityData.state_abbr}</p>
                  <div className="flex items-center gap-2">
                    <WeatherIcon className="w-6 h-6 text-[#f9ab00]" />
                    <span className="text-2xl font-medium text-[#202124]">{weather.temperature}°F</span>
                  </div>
                  <p className="text-sm text-[#5f6368]">{weather.condition}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#5f6368] uppercase">{weather.season}</p>
                </div>
              </div>

              {/* Seasonal Demand */}
              {seasonalDemand.demand && (
                <div className={`p-3 rounded-xl mb-4 ${
                  seasonalDemand.demand === 'very high' ? 'bg-red-50' :
                  seasonalDemand.demand === 'high' ? 'bg-orange-50' :
                  seasonalDemand.demand === 'medium' ? 'bg-yellow-50' : 'bg-green-50'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className={`w-4 h-4 ${
                      seasonalDemand.demand === 'very high' ? 'text-red-600' :
                      seasonalDemand.demand === 'high' ? 'text-orange-600' :
                      seasonalDemand.demand === 'medium' ? 'text-yellow-600' : 'text-green-600'
                    }`} />
                    <span className="text-sm font-medium capitalize">{seasonalDemand.demand} Demand</span>
                  </div>
                  <p className="text-xs text-[#5f6368]">{seasonalDemand.reason}</p>
                </div>
              )}

              <p className="text-sm text-[#5f6368]">
                {seoData?.seasonal_insight}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Local Insights Section */}
      <section className="py-12 bg-[#f8f9fa]">
        <div className="container-custom">
          <h2 className="text-2xl font-medium text-[#202124] mb-6">
            Local {serviceName} Insights for {cityData.city}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Local Conditions */}
            <div className="bg-white rounded-xl p-6 border border-[#dadce0]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#e8f0fe] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#1a73e8]" />
                </div>
                <h3 className="font-medium text-[#202124]">Local Conditions</h3>
              </div>
              <p className="text-[#5f6368]">
                {seoData?.local_condition || `${cityData.city}'s ${cityData.climate} climate affects ${serviceName.toLowerCase()} services in unique ways.`}
              </p>
            </div>

            {/* Building Codes */}
            <div className="bg-white rounded-xl p-6 border border-[#dadce0]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#e6f4ea] flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#1e8e3e]" />
                </div>
                <h3 className="font-medium text-[#202124]">Permits & Regulations</h3>
              </div>
              <p className="text-[#5f6368]">
                {buildingCodes.permits_required || `Most ${serviceName.toLowerCase()} work in ${cityData.city} requires proper permits.`}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Top Contractors Section */}
      <section className="py-12">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-medium text-[#202124] mb-2">
            Top {serviceName} Contractors in {cityData.city}, {cityData.state_abbr}
          </h2>
          <p className="text-[#5f6368] mb-8">
            Verified professionals ready to help with your {serviceName.toLowerCase()} needs
          </p>

          {workers.length === 0 ? (
            <div className="text-center py-12 bg-[#f8f9fa] rounded-2xl">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-[#202124] mb-2">No contractors found yet</h3>
              <p className="text-[#5f6368] mb-6">
                We're expanding to {cityData.city}. Be the first to join!
              </p>
              <Button
                onClick={() => navigate("/join")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
              >
                Join as a Pro
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workers.map((worker) => (
                <Link
                  key={worker.id}
                  to={`/profile/${worker.id}`}
                  className="bg-white rounded-2xl border border-[#dadce0] p-6 hover:shadow-lg transition-shadow"
                  data-testid={`worker-card-${worker.id}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-[#e8f0fe] flex items-center justify-center overflow-hidden shrink-0">
                      {worker.profile_photo ? (
                        <img src={worker.profile_photo} alt={`${worker.name} - ${serviceName} contractor in ${cityData.city}`} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl font-medium text-[#1a73e8]">
                          {worker.name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-[#202124] truncate">
                          {worker.business_name || worker.name}
                        </h3>
                        {worker.is_verified && (
                          <BadgeCheck className="w-4 h-4 text-[#1a73e8] shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Star className="w-4 h-4 text-[#f9ab00] fill-[#f9ab00]" />
                        <span className="text-sm text-[#202124]">{worker.rating?.toFixed(1) || "New"}</span>
                        <span className="text-sm text-[#5f6368]">({worker.reviews_count || 0} reviews)</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-[#5f6368] mb-4 line-clamp-2">{worker.description}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-[#dadce0]">
                    <span className="text-sm text-[#5f6368] flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {worker.location}
                    </span>
                    <span className="font-medium text-[#1a73e8]">${worker.hourly_rate}/hr</span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {workers.length > 0 && (
            <div className="text-center mt-8">
              <Button
                variant="outline"
                onClick={() => navigate(`/browse?service=${service}`)}
                className="rounded-full border-[#dadce0]"
              >
                View All {serviceName} Contractors
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Open Jobs Section */}
      {jobs.length > 0 && (
        <section className="py-12 bg-[#f8f9fa]">
          <div className="container-custom">
            <h2 className="text-2xl font-medium text-[#202124] mb-2">
              Latest {serviceName} Jobs in {cityData.city}
            </h2>
            <p className="text-[#5f6368] mb-8">
              Open opportunities for {serviceName.toLowerCase()} contractors
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white rounded-xl border border-[#dadce0] p-6"
                >
                  <h3 className="font-medium text-[#202124] mb-2">{job.title}</h3>
                  <p className="text-sm text-[#5f6368] mb-4 line-clamp-2">{job.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#5f6368] flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {job.location}
                    </span>
                    <Link
                      to="/jobs"
                      className="text-[#1a73e8] text-sm hover:underline"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button
                onClick={() => navigate("/jobs")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
              >
                View All Jobs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Building Codes Section */}
      <section className="py-12">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl font-medium text-[#202124] mb-6">
            {serviceName} Building Codes & Requirements in {cityData.city}
          </h2>
          
          <div className="bg-[#f8f9fa] rounded-2xl p-6 space-y-4">
            {buildingCodes.permits_required && (
              <div>
                <h3 className="font-medium text-[#202124] mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#1a73e8]" />
                  Permit Requirements
                </h3>
                <p className="text-[#5f6368] pl-7">{buildingCodes.permits_required}</p>
              </div>
            )}
            {buildingCodes.code_authority && (
              <div>
                <h3 className="font-medium text-[#202124] mb-2 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#1a73e8]" />
                  Code Authority
                </h3>
                <p className="text-[#5f6368] pl-7">{buildingCodes.code_authority}</p>
              </div>
            )}
            {buildingCodes.inspection_info && (
              <div>
                <h3 className="font-medium text-[#202124] mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#1a73e8]" />
                  Inspections
                </h3>
                <p className="text-[#5f6368] pl-7">{buildingCodes.inspection_info}</p>
              </div>
            )}
            {buildingCodes.special_requirements && (
              <div>
                <h3 className="font-medium text-[#202124] mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#1a73e8]" />
                  Special Requirements
                </h3>
                <p className="text-[#5f6368] pl-7">{buildingCodes.special_requirements}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-[#f8f9fa]">
        <div className="container-custom max-w-3xl">
          <h2 className="text-2xl font-medium text-[#202124] text-center mb-8">
            Frequently Asked Questions About {serviceName} in {cityData.city}
          </h2>
          
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="cost" className="bg-white rounded-xl border border-[#dadce0] px-6">
              <AccordionTrigger className="text-left font-medium text-[#202124]">
                How much does a {serviceName.toLowerCase()} cost in {cityData.city}, {cityData.state_abbr}?
              </AccordionTrigger>
              <AccordionContent className="text-[#5f6368]">
                The average {serviceName.toLowerCase()} professional in {cityData.city}, {cityData.state_abbr} charges between $50-150 per hour depending on the job complexity. Simple repairs may cost $75-200 total, while larger projects can range from $200-1000+. Get free quotes from verified contractors on LocalPros to compare prices.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="find" className="bg-white rounded-xl border border-[#dadce0] px-6">
              <AccordionTrigger className="text-left font-medium text-[#202124]">
                How do I find a reliable {serviceName.toLowerCase()} in {cityData.city}?
              </AccordionTrigger>
              <AccordionContent className="text-[#5f6368]">
                LocalPros connects you with verified, background-checked {serviceName.toLowerCase()} professionals in {cityData.city}. Browse reviews from real customers, compare quotes, and book directly. All our contractors are licensed and insured for work in {cityData.state}.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="license" className="bg-white rounded-xl border border-[#dadce0] px-6">
              <AccordionTrigger className="text-left font-medium text-[#202124]">
                Do {serviceName.toLowerCase()} contractors in {cityData.city} need a license?
              </AccordionTrigger>
              <AccordionContent className="text-[#5f6368]">
                Yes, {cityData.city}, {cityData.state_abbr} requires contractors to be licensed through the Arizona Registrar of Contractors (ROC) for work over $1,000. All LocalPros contractors are verified and meet local licensing requirements.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="time" className="bg-white rounded-xl border border-[#dadce0] px-6">
              <AccordionTrigger className="text-left font-medium text-[#202124]">
                What's the best time to hire a {serviceName.toLowerCase()} in {cityData.city}?
              </AccordionTrigger>
              <AccordionContent className="text-[#5f6368]">
                {seoData?.local_condition || `The best time varies by season. In ${cityData.city}'s ${cityData.climate} climate, many homeowners schedule preventive maintenance in spring and fall when temperatures are moderate and contractors are less busy with emergency calls.`}
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="permits" className="bg-white rounded-xl border border-[#dadce0] px-6">
              <AccordionTrigger className="text-left font-medium text-[#202124]">
                Are permits required for {serviceName.toLowerCase()} work in {cityData.city}?
              </AccordionTrigger>
              <AccordionContent className="text-[#5f6368]">
                {buildingCodes.permits_required || `Most significant ${serviceName.toLowerCase()} work in ${cityData.city} requires permits from the local building department. Your contractor can advise on specific permit requirements for your project.`}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Nearby Cities */}
      <section className="py-12">
        <div className="container-custom">
          <h2 className="text-2xl font-medium text-[#202124] text-center mb-2">
            {serviceName} Services in Nearby Cities
          </h2>
          <p className="text-[#5f6368] text-center mb-8">
            We also serve these areas near {cityData.city}
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {cityData.nearby_cities?.map((nearbyCity) => {
              const nearbySlug = `${nearbyCity.toLowerCase().replace(/\s+/g, '-')}-az`;
              return (
                <Link
                  key={nearbyCity}
                  to={`/${nearbySlug}/${service}`}
                  className="px-4 py-2 bg-[#f8f9fa] text-[#5f6368] rounded-full hover:bg-[#e8f0fe] hover:text-[#1a73e8] transition-colors text-sm"
                >
                  {serviceName} in {nearbyCity}
                </Link>
              );
            })}
          </div>
          
          <div className="mt-8">
            <h3 className="text-lg font-medium text-[#202124] text-center mb-4">
              Other Services in {cityData.city}
            </h3>
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(serviceNames)
                .filter(([slug]) => slug !== service)
                .slice(0, 6)
                .map(([slug, name]) => (
                  <Link
                    key={slug}
                    to={`/${citySlug}/${slug}`}
                    className="px-4 py-2 bg-[#f8f9fa] text-[#5f6368] rounded-full hover:bg-[#e8f0fe] hover:text-[#1a73e8] transition-colors text-sm"
                  >
                    {name} in {cityData.city}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1a73e8]">
        <div className="container-custom text-center text-white">
          <h2 className="text-2xl md:text-3xl font-medium mb-4">
            Ready to Find a {serviceName} Pro in {cityData.city}?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Get matched with top-rated local professionals. Free quotes, no obligation.
          </p>
          <Button
            onClick={() => navigate(`/request?service=${service}&location=${cityData.city}`)}
            className="bg-white text-[#1a73e8] hover:bg-[#f8f9fa] rounded-full px-8 py-3 h-auto text-base"
          >
            Get Started - It's Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-12">
        <div className="container-custom max-w-3xl">
          <h2 className="text-2xl font-medium text-[#202124] mb-6">
            About {serviceName} Services in {cityData.city}, {cityData.state}
          </h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-[#5f6368] mb-4">
              Finding reliable {serviceName.toLowerCase()} services in {cityData.city}, {cityData.state_abbr} doesn't have to be difficult. LocalPros connects homeowners with verified, experienced {serviceName.toLowerCase()} professionals serving {cityData.city} and the greater {cityData.state} area, including {cityData.nearby_cities?.slice(0, 4).join(", ")}.
            </p>
            <p className="text-[#5f6368] mb-4">
              {seoData?.local_condition}
            </p>
            <p className="text-[#5f6368] mb-4">
              With {cityData.population} residents in the {cityData.city} metro area, there's strong demand for quality {serviceName.toLowerCase()} services. Our network of {stats.workers_count || 10}+ verified contractors maintains an average rating of {stats.avg_rating || 4.5} stars, ensuring you get reliable service every time.
            </p>
            
            <h3 className="text-xl font-medium text-[#202124] mt-8 mb-4">
              Why Choose LocalPros for {serviceName} in {cityData.city}?
            </h3>
            <ul className="space-y-2 text-[#5f6368]">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Verified and background-checked {cityData.city} professionals</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Real customer reviews from {cityData.city} homeowners</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Free quotes with no obligation</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Licensed and insured for {cityData.state} work</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1e8e3e] shrink-0 mt-0.5" />
                <span>Fast response times from local contractors</span>
              </li>
            </ul>
            
            <h3 className="text-xl font-medium text-[#202124] mt-8 mb-4">
              {cityData.city} Service Areas
            </h3>
            <p className="text-[#5f6368] mb-4">
              Our {serviceName.toLowerCase()} contractors serve all of {cityData.city} including zip codes: {cityData.zip_codes?.slice(0, 10).join(", ")}, and more. We also cover nearby cities: {cityData.nearby_cities?.join(", ")}.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CityServicePage;
